/**
 * "Add to collection…" from a sound's ⋯ menu: a small dialog listing the team's collections, a checkmark on the ones
 * that already hold the sound. Clicking a collection adds the sound (POST /collections/{id}/tracks) or, when it is
 * already in it, takes it out again. "+ New collection" creates one inline with the sound already in it.
 * Membership comes from each collection's detail (teams have a handful of collections; the details are cached and
 * shared with the Collections tab).
 */
import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Check, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/components/ui/utils';
import type { StudioTrack } from '@/api/audioStudioApi';
import { COLLECTION_NAME_MAX, collectionErrorMessage, useCollectionMembership, useCreateCollection } from './collectionMutations';
import { collectionQueryOptions, useCollections } from './queries';

export function AddToCollectionDialog({ track, open, onOpenChange }: { track: StudioTrack | null; open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        {/* Keyed on the sound so a new one starts with a closed "New collection" form. */}
        {track && <Picker key={track.track_id} track={track} />}
      </DialogContent>
    </Dialog>
  );
}

function Picker({ track }: { track: StudioTrack }) {
  const title = track.title || 'Untitled';
  const collections = useCollections();
  const list = collections.data ?? [];
  const details = useQueries({ queries: list.map((c) => collectionQueryOptions(c.collection_id)) });
  const membership = useCollectionMembership();
  const create = useCreateCollection();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const toggle = (collectionId: string, collectionName: string, add: boolean) => {
    membership.mutate(
      { collectionId, trackId: track.track_id, add },
      {
        onSuccess: () => toast.success(add ? `Added to “${collectionName}”` : `Removed from “${collectionName}”`),
        onError: (e) => toast.error(collectionErrorMessage(e)),
      },
    );
  };

  const submitNew = () => {
    const n = name.trim();
    if (!n) return;
    create.mutate(
      { name: n, track_ids: [track.track_id] },
      {
        onSuccess: (c) => { toast.success(`Added to “${c.name}”`); setCreating(false); setName(''); setError(null); },
        onError: (e) => setError(collectionErrorMessage(e)),
      },
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-[16px]">Add to collection</DialogTitle>
        <DialogDescription className="truncate text-[12.5px]">“{title}”</DialogDescription>
      </DialogHeader>

      {collections.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-6 text-[12.5px] text-[#667085]" role="status"><Loader2 className="h-4 w-4 animate-spin text-[#98a2b3]" aria-hidden /> Loading collections…</div>
      ) : collections.isError ? (
        <p className="py-4 text-[12.5px] text-[#b42318]" role="alert">Could not load collections: {(collections.error as Error).message}</p>
      ) : list.length === 0 ? (
        <p className="py-2 text-[12.5px] text-[#667085]">No collections yet: create the first one below.</p>
      ) : (
        <ul className="-mx-1 max-h-[300px] space-y-0.5 overflow-y-auto" aria-label="Collections">
          {list.map((c, i) => {
            const detail = details[i]?.data;
            const inIt = Boolean(detail?.tracks.some((t) => t.track_id === track.track_id));
            const busy = membership.isPending && membership.variables?.collectionId === c.collection_id;
            return (
              <li key={c.collection_id}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={inIt}
                  disabled={!detail || busy}
                  onClick={() => toggle(c.collection_id, c.name, !inIt)}
                  className="flex h-10 w-full items-center gap-2.5 rounded-[8px] px-2 text-left hover:bg-[#f9fafb] disabled:opacity-60"
                >
                  <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] border', inIt ? 'border-[#6941c6] bg-[#6941c6] text-white' : 'border-[#d0d5dd] bg-white')}>
                    {busy || !detail ? <Loader2 className="h-3 w-3 animate-spin text-[#98a2b3]" aria-hidden /> : inIt && <Check className="h-3.5 w-3.5" aria-hidden />}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13.5px] text-[#101828]" style={{ fontWeight: 500 }}>{c.name}</span>
                  <span className="shrink-0 text-[12px] tabular-nums text-[#98a2b3]">{c.track_count}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {creating ? (
        <form className="space-y-1.5" onSubmit={(e) => { e.preventDefault(); submitNew(); }}>
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={name}
              onChange={(e) => { setName(e.target.value); setError(null); }}
              onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setCreating(false); setError(null); } }}
              maxLength={COLLECTION_NAME_MAX}
              placeholder="Collection name"
              aria-label="New collection name"
              aria-invalid={Boolean(error)}
              className="h-9 min-w-0 flex-1 rounded-[8px] border border-[#d0d5dd] px-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#98a2b3]/20"
            />
            <Button type="submit" size="sm" disabled={!name.trim() || create.isPending} className="h-9 rounded-[8px] bg-[#be43ff] px-3 text-[13px] text-white hover:bg-[#a835e0]">
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Create
            </Button>
          </div>
          {error && <p className="text-[12px] text-[#b42318]" role="alert">{error}</p>}
        </form>
      ) : (
        <button type="button" onClick={() => setCreating(true)} className="inline-flex h-9 items-center gap-1.5 self-start rounded-[8px] px-2 text-[13px] text-[#6941c6] hover:bg-[#f9fafb]" style={{ fontWeight: 600 }}>
          <Plus className="h-4 w-4" aria-hidden /> New collection
        </button>
      )}
    </>
  );
}
